import { useMemo } from 'react';
import {
  Modal,
  Typography,
  TypographyVariants,
  TypographyWeights,
  TypographyColors,
  ButtonVariants,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';

/**
 * Props:
 *   open          whether the dialog is shown
 *   title         the modal header
 *   bodyTitle     the headline inside the body
 *   description   supporting copy
 *   confirmLabel  primary button text
 *   cancelLabel   secondary button text
 *   extraLabel    optional third button, drawn between cancel and confirm
 *   onExtra       () => void — required for the third button to render
 *   tone          'default' | 'danger'
 *   onConfirm     () => void
 *   onCancel      () => void
 */
export const ConfirmDialog = ({
  open,
  title,
  bodyTitle,
  description,
  confirmLabel,
  cancelLabel,
  extraLabel,
  onExtra,
  tone = 'default', // 'default' | 'danger'
  onConfirm,
  onCancel,
}) => {
  const t = useTranslations();

  const buttons = useMemo(
    () => [
      {
        label: cancelLabel ?? t('COMMON_CANCEL'),
        variant: ButtonVariants.neutral,
        onClick: onCancel,
      },
      ...(onExtra
        ? [{ label: extraLabel, variant: ButtonVariants.secondary, onClick: onExtra }]
        : []),
      {
        label: confirmLabel ?? t('COMMON_CONFIRM'),
        variant: tone === 'danger' ? ButtonVariants.danger : ButtonVariants.primary,
        onClick: onConfirm,
      },
    ],
    [cancelLabel, confirmLabel, extraLabel, tone, onCancel, onConfirm, onExtra, t],
  );

  return (
    <Modal open={open} onClose={onCancel} title={title} buttons={buttons}>
      {bodyTitle && (
        <Typography variant={TypographyVariants.bodySm} weight={TypographyWeights.semibold}>
          {bodyTitle}
        </Typography>
      )}
      {description && (
        <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
          {description}
        </Typography>
      )}
    </Modal>
  );
};
